import { Autocomplete, Grid, TextField } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import useCrud from "hooks/useCrud";
import { ARTICLE_LOCATIONS_URL } from "constants/urls";

export type ArticleLocationLinkedFiltersValue = {
  locationId?: number;
  articleCode: string;
  lote: string;
  oc: string;
};

type FilterOptions = {
  locations: { id: number; code: string }[];
  articles: { id: number; code: string; description: string }[];
  lotes: string[];
  ocs: string[];
};

const EMPTY_FILTER_OPTIONS: FilterOptions = {
  locations: [],
  articles: [],
  lotes: [],
  ocs: [],
};

type Props = {
  clientId?: number | null;
  value: ArticleLocationLinkedFiltersValue;
  onChange: (value: ArticleLocationLinkedFiltersValue) => void;
  disabled?: boolean;
  movementId?: number | null;
  storeId?: number | null;
};

export default function ArticleLocationFilters({
  clientId,
  value,
  onChange,
  disabled = false,
  movementId,
  storeId,
}: Props) {
  const {
    data,
    fetchData,
    loading,
  } = useCrud<FilterOptions>(`${ARTICLE_LOCATIONS_URL}filter-options`, {
    disableAutoFetch: true,
  });
  const fetchDataRef = useRef(fetchData);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const filtersQuery = useMemo(() => {
    if (!clientId) return "";

    const query = new URLSearchParams({
      clientId: String(clientId),
    });

    if (value.locationId) query.set("locationId", String(value.locationId));
    if (value.articleCode) query.set("articleCode", value.articleCode);
    if (value.lote) query.set("lote", value.lote);
    if (value.oc) query.set("oc", value.oc);
    if (movementId) query.set("movementId", String(movementId));
    if (storeId) query.set("storeId", String(storeId));

    return `?${query.toString()}`;
  }, [
    clientId,
    movementId,
    storeId,
    value.articleCode,
    value.locationId,
    value.lote,
    value.oc,
  ]);

  useEffect(() => {
    if (!filtersQuery) return;

    void fetchDataRef.current(filtersQuery, true);
  }, [filtersQuery]);

  const options = useMemo<FilterOptions>(
    () => {
      const responseData = data as unknown as FilterOptions | FilterOptions[];

      return Array.isArray(responseData)
        ? EMPTY_FILTER_OPTIONS
        : responseData ?? EMPTY_FILTER_OPTIONS;
    },
    [data],
  );

  const loteOptions = useMemo(
    () => options.lotes.map((label) => ({ label })),
    [options.lotes],
  );

  const ocOptions = useMemo(
    () => options.ocs.map((label) => ({ label })),
    [options.ocs],
  );

  const selectedLocation =
    options.locations.find((location) => location.id === value.locationId) ?? null;

  const selectedArticle =
    options.articles.find((article) => article.code === value.articleCode) ?? null;

  const selectedLote = useMemo(
    () => loteOptions.find((option) => option.label === value.lote) ?? null,
    [loteOptions, value.lote],
  );

  const selectedOc = useMemo(
    () => ocOptions.find((option) => option.label === value.oc) ?? null,
    [ocOptions, value.oc],
  );

  const isDisabled = disabled || !clientId || loading;

  return (
    <Grid container spacing={1} mt={1}>
      <Grid item xs={12} md={3}>
        <Autocomplete
          disabled={isDisabled}
          options={options.locations}
          value={selectedLocation}
          getOptionLabel={(option) => option.code}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          onChange={(_, next) =>
            onChange({ ...value, locationId: next?.id })
          }
          renderInput={(params) => <TextField {...params} label="Ubicación" />}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Autocomplete
          disabled={isDisabled}
          options={options.articles}
          value={selectedArticle}
          getOptionLabel={(option) =>
            option.description
              ? `${option.code} - ${option.description}`
              : option.code
          }
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          onChange={(_, next) =>
            onChange({ ...value, articleCode: next?.code ?? "" })
          }
          renderInput={(params) => <TextField {...params} label="Artículo" />}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Autocomplete
          disabled={isDisabled}
          options={loteOptions}
          value={selectedLote}
          isOptionEqualToValue={(option, selected) => option.label === selected.label}
          onChange={(_, next) =>
            onChange({ ...value, lote: next?.label ?? "" })
          }
          renderInput={(params) => <TextField {...params} label="Lote" />}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Autocomplete
          disabled={isDisabled}
          options={ocOptions}
          value={selectedOc}
          isOptionEqualToValue={(option, selected) => option.label === selected.label}
          onChange={(_, next) =>
            onChange({ ...value, oc: next?.label ?? "" })
          }
          renderInput={(params) => <TextField {...params} label="OC" />}
        />
      </Grid>
    </Grid>
  );
}
